import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useCallback, useRef } from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { isEqual } from 'lodash';
import { FormCheckbox, Modal } from '@praktio/common-ui';
import { Utils } from '@praktio/common';
import 'react-toastify/dist/ReactToastify.css';

import './App.scss';

import AdminDashboard from './components/admin/Dashboard';
import AdminCourses from './components/admin/Courses';
import Users from './components/admin/Users';
import Logs from './components/admin/Logs';
import Settings from './components/admin/Settings';
import Course from './components/admin/Course/Course';
import Module from './components/admin/Module';
import GeneralReport from './components/admin/GeneralReport/GeneralReport';
import CLEReport from './components/admin/CLEReport/CLEReport';
import Online from './components/admin/Online/Online';

import Dashboard from './components/Dashboard';
import Login from './components/Login/Login';
import TopNav from './components/TopNav';

import BulkUploadUsers from './components/admin/BulkUploadUsers';
import BulkEditUsers from './components/admin/BulkEditUsers';
import CourseViewer from './components/learner/CourseViewer';
import UserSettings from './components/learner/Settings/Settings';

import Team from './components/admin/Team/Team';
import Teams from './components/admin/Teams';
import TeamManagerReport from './components/manager/Reports/TeamManagerReport';
import SelfSignup from './components/SelfSignup/SelfSignup';
import Register from './components/Register/Register';
import ForgotPassword from './components/learner/ForgotPassword';
import ResetPassword from './components/learner/ResetPassword';
import AppLevelOverlaySpinner from './components/common/AppLevelOverlaySpinner/AppLevelOverlaySpinner';
import OverlaySpinner from './components/common/OverlaySpinner/OverlaySpinner';
import PresenceHeartbeat from './components/common/PresenceHeartbeat/PresenceHeartbeat';
import { checkAuthed, updateMaintenanceNotified } from './ducks/auth';
import RequireAuth from './components/RequireAuth';
import { RootState } from './ducks/store';
import {
  getPresenceContext,
  isPlatformAdmin,
  shouldShowFullOverlay,
  useQuery,
} from './utils/functions';
import { getPlatformData } from './ducks/platformData';
import {
  InactiveDuration,
  LogoutTypes,
  ModalTypes,
  ThirtyMinutes,
  ThirtySeconds,
  UnauthedRoutes,
} from './utils/constants';

// Modals
import { showModal } from './ducks/modal';
import LoginSSO from './components/common/LoginSSO/LoginSSO';
import Maintenance from './components/Maintenance/Maintenance';

const { UserAgentParser } = Utils;
const userAgentResults = UserAgentParser.isValidBrowser();

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const showLogin = query.get('showLogin') === '1';

  const inactiveTimeoutId = useRef<NodeJS.Timeout | undefined>();
  const maintenanceTimeoutId = useRef<NodeJS.Timeout | undefined>();
  const prevMaintenance = useRef<IMaintenance | undefined>();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [unsupportedModeConfirmed, setUnsupportedModeConfirmed] = useState(false);
  const [confirmUnsupportedModeChecked, setConfirmUnsupportedModeChecked] = useState(false);

  const location = useLocation();
  const {
    logoutType,
    modalType,
    maintenanceNotified,
    maintenance,
    isAdmin,
    authenticatedUser,
    course,
  } = useSelector((state: RootState) => {
    const { authenticatedUser, maintenanceNotified, logoutType } = state.auth;
    const { maintenance } = state.platformData;
    const { showType } = state.modal;
    const isAdmin = isPlatformAdmin(authenticatedUser);
    return {
      logoutType,
      modalType: showType,
      maintenanceNotified,
      maintenance,
      isAdmin,
      authenticatedUser,
      course: state.learner.course,
    };
  });

  const isUnauthedPath = location.pathname ? !!location?.pathname?.match(UnauthedRoutes) : true;
  const recordActivity = useCallback(() => {
    const timeoutCallback = () => {
      if (
        logoutType !== LogoutTypes.Inactivity &&
        modalType !== ModalTypes.Inactivity &&
        !isUnauthedPath
      ) {
        dispatch(showModal(ModalTypes.Inactivity));
      }
    };

    if (inactiveTimeoutId.current) {
      clearTimeout(inactiveTimeoutId.current);
      inactiveTimeoutId.current = undefined;
    }

    if (
      logoutType !== LogoutTypes.Inactivity &&
      modalType !== ModalTypes.Inactivity &&
      !isUnauthedPath
    ) {
      inactiveTimeoutId.current = setTimeout(timeoutCallback, InactiveDuration);
    }
  }, [isUnauthedPath, logoutType, modalType, dispatch]);

  useEffect(() => {
    const getData = async () => {
      await Promise.all([
        dispatch(getPlatformData(navigate)),
        showLogin ? Promise.resolve() : (dispatch(checkAuthed()) as any),
      ]);

      setCheckingAuth(false);
    };

    getData();

    // Omitting any dependencies here because we only want this to run on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sameAsPrev = isEqual(maintenance, prevMaintenance.current);
    if (
      !isAdmin &&
      !!maintenance &&
      maintenance.start &&
      modalType !== ModalTypes.Maintenance &&
      (!maintenanceNotified || !sameAsPrev)
    ) {
      const startTime = new Date(maintenance.start).getTime();
      const nowTime = new Date().getTime();
      const difference = startTime - nowTime;
      if (difference > 0 && difference <= ThirtyMinutes) {
        dispatch(showModal(ModalTypes.Maintenance));
      }

      // Set to display again when less than 30 seconds remain
      if (difference > ThirtySeconds && (!maintenanceTimeoutId.current || !sameAsPrev)) {
        if (maintenanceTimeoutId.current) {
          clearTimeout(maintenanceTimeoutId.current);
        }

        maintenanceTimeoutId.current = setTimeout(() => {
          dispatch(showModal(ModalTypes.MaintenanceCountdown));
        }, difference - ThirtySeconds);
      }

      if (!prevMaintenance.current || !sameAsPrev) {
        dispatch(updateMaintenanceNotified(false));
        prevMaintenance.current = maintenance;
      }
    }
  }, [maintenance, modalType, maintenanceNotified, isAdmin, dispatch]);

  if (checkingAuth) {
    return shouldShowFullOverlay({ checkingAuth, isUnauthedPath, showLogin }) ? (
      <OverlaySpinner className="AppLevel" />
    ) : null;
  }

  // App recomputes the presence "Activity" label from the current path on every
  // render so each heartbeat carries it. What keeps the label stable across a
  // reconnect is the onopen flush of the last heartbeat in websocket.ts;
  // CourseViewer sends the same value for the in-iframe activity case.
  const presenceContext = getPresenceContext(location.pathname, course);

  return (
    <div
      className="container-fluid p-0 App"
      onMouseOver={recordActivity}
      onMouseMove={recordActivity}
      onMouseDown={recordActivity}
      onKeyDown={recordActivity}
      onFocus={recordActivity}
    >
      <TopNav />
      {authenticatedUser && <PresenceHeartbeat context={presenceContext} />}
      <DndProvider backend={HTML5Backend}>
        <div className="App-content">
          <Routes>
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/signup" element={<SelfSignup />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/sso" element={<LoginSSO />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="/admin" element={<RequireAuth checkAdmin />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/courses/:courseId/modules/:moduleId" element={<Module />} />
              <Route path="/admin/courses/:courseId" element={<Course />} />
              <Route path="/admin/courses" element={<AdminCourses />} />
              <Route path="/admin/users/bulk/edit" element={<BulkEditUsers />} />
              <Route path="/admin/users/bulk" element={<BulkUploadUsers />} />
              <Route path="/admin/users/:userId/courses/:courseId" element={<Users />} />
              <Route path="/admin/users/:userId" element={<Users />} />
              <Route path="/admin/users" element={<Users />} />
              <Route path="/admin/teams/:teamId" element={<Team />} />
              <Route path="/admin/teams" element={<Teams />} />
              <Route path="/admin/logs" element={<Logs />} />
              <Route path="/admin/settings" element={<Settings />} />
              <Route path="/admin/reports/general" element={<GeneralReport />} />
              <Route path="/admin/reports/cle" element={<CLEReport />} />
              <Route path="/admin/online" element={<Online />} />
            </Route>
            <Route
              path={`/manager/reports`}
              element={
                <RequireAuth checkManager>
                  <TeamManagerReport />
                </RequireAuth>
              }
            />
            <Route
              path={`/settings`}
              element={
                <RequireAuth isSettings>
                  <UserSettings />
                </RequireAuth>
              }
            />
            {[
              `/courses/:courseId`,
              `/courses/:courseId/modules/:moduleId`,
              `/courses/:courseId/modules/:moduleId/slides/:slideId`,
            ].map((path, i) => (
              <Route
                key={`viewer-${i}`}
                path={path}
                element={
                  <RequireAuth>
                    <CourseViewer />
                  </RequireAuth>
                }
              />
            ))}
            <Route
              path="*"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
          </Routes>
        </div>
      </DndProvider>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        transition={Slide}
        closeOnClick={false}
        hideProgressBar
        closeButton={false}
      />
      <AppLevelOverlaySpinner />
      <Modal
        className="UnsupportedModeModal"
        show={!userAgentResults?.valid && !unsupportedModeConfirmed}
        title="Unsupported Device or Browser"
        description={userAgentResults?.notice}
        hideActionIcons
        backdrop
        disableSave={!confirmUnsupportedModeChecked}
        saveText="PROCEED"
        onSave={() => setUnsupportedModeConfirmed(true)}
      >
        <FormCheckbox
          id="unsupported-confirmation"
          label="I understand that proceeding with this unsupported device or browser might result in the site not performing as intended."
          checked={confirmUnsupportedModeChecked}
          onChange={() => setConfirmUnsupportedModeChecked(!confirmUnsupportedModeChecked)}
        />
      </Modal>
    </div>
  );
};

export default App;
